<!--
  CollectorSyncPill — the collector's header pill (handoff spec 2.1 / 2.4):
  green «متزامن» when our server answers and nothing waits, «بيتزامن · N» while
  taps are on the wire, amber «أوفلاين · N علامات مستنية النت» when it does not
  answer, amber «مستني تيليجرام · N» while the queue waits for a login
  (collector-app P2-B, L-D — the card on the page says what to do).
  Online/offline is the ONE shared detector pointed at our API
  (collector/network.js) — held while this pill is on screen.

  «متزامن» is said only when NOTHING is left on the phone (collector-offline
  P2-A): the action layer's unanswered taps, the shop invoices filed and not
  sent (collector/invoiceQueue.js) and the shelf photos not uploaded
  (collector/photoQueue.js) — all restored from the phone after a restart.
  A tap or a filing waiting is counted by the marks' words (N = taps + filings);
  when only photos wait they have words of their own; paused on a login, N is
  everything waiting.
-->
<template>
  <div
    class="c-sync-pill"
    :class="[online && !paused ? 'c-sync-pill--online' : 'c-sync-pill--offline', { 'c-sync-pill--compact': compact }]"
    role="status"
    :data-online="online"
  >
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2.4"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12.5A11 11 0 0 1 19 12.5" />
      <path d="M8.5 16a6 6 0 0 1 7 0" />
      <circle cx="12" cy="19.5" r="1" />
      <path v-if="!online || paused" d="M3 3l18 18" />
    </svg>
    <span>{{ label }}</span>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCollectorActionsStore } from '@/collector/actions.js';
import { countPhrase } from '@/collector/dayView.js';
import { useCollectorInvoicesStore } from '@/collector/invoiceQueue.js';
import { useCollectorNetwork } from '@/collector/network.js';
import { useCollectorPhotosStore } from '@/collector/photoQueue.js';
import { authPaused } from '@/collector/session.js';

defineProps({
  /** The shop header's smaller pill. */
  compact: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const network = useCollectorNetwork();
const actions = useCollectorActionsStore();
const invoices = useCollectorInvoicesStore();
const photos = useCollectorPhotosStore();

const online = computed(() => network.isOnline.value === true);
const paused = computed(() => authPaused.value === true);

const label = computed(() => {
  // His marks and the invoices he filed speak the marks' words; photos have their own.
  const marks = actions.pendingCount + invoices.pendingCount;
  const pictures = photos.pendingCount;
  const everything = marks + pictures;
  if (paused.value) {
    return everything > 0 ? t('collector.sync.paused', { n: everything }) : t('collector.sync.pausedEmpty');
  }
  if (!online.value) {
    if (everything === 0) return t('collector.sync.offline');
    const phrase = marks > 0 ? countPhrase('offlineWaiting', marks) : countPhrase('offlinePhotos', pictures);
    return t(phrase.key, phrase.params);
  }
  if (everything === 0) return t('collector.sync.synced');
  return marks > 0 ? t('collector.sync.syncing', { n: marks }) : t('collector.sync.photos', { n: pictures });
});
</script>

<style scoped>
.c-sync-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 7px 12px;
  border-radius: 999px;
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}

.c-sync-pill--online {
  background: var(--c-green-pill, #15734f);
}

.c-sync-pill--offline {
  background: var(--c-amber-pill, #b45309);
  font-weight: 500;
}

.c-sync-pill--compact {
  padding: 7px 10px;
  font-size: 12px;
}
</style>
